import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Gift } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type Draft = {
  titleLt: string;
  titleEn: string;
  descriptionLt: string;
  descriptionEn: string;
  amount: string;
};

const emptyDraft: Draft = {
  titleLt: "",
  titleEn: "",
  descriptionLt: "",
  descriptionEn: "",
  amount: "",
};

function trimDraft(d: Draft): Draft {
  return {
    titleLt: d.titleLt.trim(),
    titleEn: d.titleEn.trim(),
    descriptionLt: d.descriptionLt.trim(),
    descriptionEn: d.descriptionEn.trim(),
    amount: d.amount.trim(),
  };
}

function isComplete(d: Draft): boolean {
  const t = trimDraft(d);
  return (
    t.titleLt.length > 0 &&
    t.titleEn.length > 0 &&
    t.descriptionLt.length > 0 &&
    t.descriptionEn.length > 0 &&
    t.amount.length > 0
  );
}

export function SettingsPerksPage() {
  const utils = trpc.useUtils();
  const list = trpc.perkCatalog.list.useQuery();

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft);

  const create = trpc.perkCatalog.create.useMutation({
    onSuccess: () => {
      utils.perkCatalog.list.invalidate();
      setDraft(emptyDraft);
      toast.success("Perk added");
    },
    onError: (err) => toast.error(err.message || "Failed to add"),
  });

  const update = trpc.perkCatalog.update.useMutation({
    onSuccess: () => {
      utils.perkCatalog.list.invalidate();
      setEditingId(null);
      toast.success("Updated");
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });

  const remove = trpc.perkCatalog.delete.useMutation({
    onSuccess: () => {
      utils.perkCatalog.list.invalidate();
      toast.success("Removed");
    },
    onError: () => toast.error("Failed to remove"),
  });

  const items = list.data ?? [];

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComplete(draft)) return;
    create.mutate(trimDraft(draft));
  };

  const startEdit = (id: string, item: Draft) => {
    setEditingId(id);
    setEditDraft(item);
  };

  const saveEdit = (id: string) => {
    if (!isComplete(editDraft)) return;
    update.mutate({ id, patch: trimDraft(editDraft) });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
      <div className="mb-6 flex items-center gap-3">
        <Gift className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Perks</h1>
          <p className="text-sm text-muted-foreground">
            Reusable perk templates that can be toggled on opportunities. The
            opportunity language picks which version (LT or EN) is shown to the
            client.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleAdd}
        className="mb-8 space-y-4 rounded-lg border bg-muted/20 p-4"
      >
        <div className="text-sm font-medium">Add a new perk</div>
        <DraftFields draft={draft} onChange={setDraft} />
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={create.isPending || !isComplete(draft)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add perk
          </Button>
        </div>
      </form>

      {list.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          No perks yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const isEditing = editingId === item.id;
            return (
              <PerkRow
                key={item.id}
                item={item}
                isEditing={isEditing}
                editDraft={editDraft}
                onEditDraftChange={setEditDraft}
                onStartEdit={() =>
                  startEdit(item.id, {
                    titleLt: item.titleLt,
                    titleEn: item.titleEn,
                    descriptionLt: item.descriptionLt,
                    descriptionEn: item.descriptionEn,
                    amount: item.amount,
                  })
                }
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={() => saveEdit(item.id)}
                onRemove={() => remove.mutate({ id: item.id })}
                saving={update.isPending}
                removing={remove.isPending}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function DraftFields({
  draft,
  onChange,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
}) {
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    onChange({ ...draft, [key]: value });
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Title — Lithuanian
          </Label>
          <Input
            value={draft.titleLt}
            placeholder="€2 000 kreditas pasirinkus metinį planą"
            onChange={(e) => set("titleLt", e.target.value)}
            maxLength={500}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Title — English
          </Label>
          <Input
            value={draft.titleEn}
            placeholder="€2 000 credit for integrations"
            onChange={(e) => set("titleEn", e.target.value)}
            maxLength={500}
          />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Description — Lithuanian
          </Label>
          <Textarea
            value={draft.descriptionLt}
            placeholder="Kreditas skiriamas funkcijoms, kurių Jums reikia…"
            onChange={(e) => set("descriptionLt", e.target.value)}
            className="min-h-[80px]"
            maxLength={2000}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Description — English
          </Label>
          <Textarea
            value={draft.descriptionEn}
            placeholder="Credit applied to integrations, automations, or trainings…"
            onChange={(e) => set("descriptionEn", e.target.value)}
            className="min-h-[80px]"
            maxLength={2000}
          />
        </div>
      </div>
      <div className="max-w-[200px] space-y-1.5">
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Amount label
        </Label>
        <Input
          value={draft.amount}
          placeholder="€2 000"
          onChange={(e) => set("amount", e.target.value)}
          maxLength={64}
        />
      </div>
    </div>
  );
}

function PerkRow({
  item,
  isEditing,
  editDraft,
  onEditDraftChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onRemove,
  saving,
  removing,
}: {
  item: {
    id: string;
    titleLt: string;
    titleEn: string;
    descriptionLt: string;
    descriptionEn: string;
    amount: string;
  };
  isEditing: boolean;
  editDraft: Draft;
  onEditDraftChange: (d: Draft) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onRemove: () => void;
  saving: boolean;
  removing: boolean;
}) {
  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancelEdit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEditing, onCancelEdit]);

  if (isEditing) {
    return (
      <div className="space-y-4 rounded-lg border p-4">
        <DraftFields draft={editDraft} onChange={onEditDraftChange} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelEdit}>
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSaveEdit}
            disabled={saving || !isComplete(editDraft)}
          >
            <Check className="mr-2 h-4 w-4" />
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                LT
              </div>
              <div className="mt-0.5 text-sm font-medium">{item.titleLt}</div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {item.descriptionLt}
              </p>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                EN
              </div>
              <div className="mt-0.5 text-sm font-medium">{item.titleEn}</div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {item.descriptionEn}
              </p>
            </div>
          </div>
          <div className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
            {item.amount}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground"
            onClick={onStartEdit}
            aria-label="Edit perk"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                disabled={removing}
                aria-label="Remove perk"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this perk?</AlertDialogTitle>
                <AlertDialogDescription>
                  This template will no longer appear in the opportunity perks
                  list. Perks already enabled on existing proposals will keep
                  their text.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={onRemove}
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
